import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type CSSProperties,
} from 'react';
import {
  characterFamilyKey,
  familyManifest,
  isFamilyRigged,
  layerTransform,
  preloadCharacterAssets,
  resolveLayer,
  selectedLayers,
  type AssetLayer,
} from '../character/manifest';
import { useCharacterRuntimeState, normalizeCharacterRuntimeState } from '../character/runtime';
import DefaultAgentRobot from './DefaultAgentRobot';
import BookwormApprovedArt from './BookwormApprovedArt';
import type {
  Agent,
  CharacterAppearance,
  CharacterBaseType,
  CharacterGender,
} from '../types';

type Props = {
  baseType: CharacterBaseType;
  appearance: CharacterAppearance;
  agent: Agent;
  previewOnly?:boolean;
};

export default function CharacterPreview({ baseType, appearance, agent, previewOnly=false }: Props) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const compositionStarted =
    typeof performance !== 'undefined' ? performance.now() : 0;

  useEffect(() => {
    preloadCharacterAssets();
  }, []);

  const family = characterFamilyKey(baseType, appearance);
  const familyConfig = familyManifest(family);
  const gender =
    familyConfig.gender_art_ready === false
      ? undefined
      : (appearance.gender as CharacterGender | undefined);
  const realState = useCharacterRuntimeState(previewOnly ? undefined : agent);
  const runtimeState = previewOnly ? normalizeCharacterRuntimeState(agent.status) : realState;
  const device = appearance.device || 'Phone';
  const rigged = isFamilyRigged(family);
  const face = familyConfig.anchors.face;
  const animationSpeed = Math.max(
    0.5,
    Math.min(2, Number(appearance.animationSpeed || 1)),
  );

  const layers = useMemo(() => {
    const baseLayers = selectedLayers(baseType, appearance);
    const expression =
      runtimeState === 'waiting_on_owner'
        ? 'expectant'
        : runtimeState === 'stalled' || runtimeState === 'failed'
          ? 'concerned'
          : runtimeState === 'working'
            ? 'focused'
            : runtimeState === 'in_review'
              ? 'attentive'
              : 'friendly';

    const expressionLayers = [
      resolveLayer(family, 'eyes', expression),
      resolveLayer(family, 'mouth', expression),
    ].filter((layer): layer is AssetLayer => Boolean(layer));

    if (runtimeState !== 'working') {
      return [...baseLayers, ...expressionLayers].sort(
        (a, b) => a.z_index - b.z_index,
      );
    }

    const runtimeDevice = resolveLayer(family, 'prop', device);
    return [
      ...baseLayers.filter(layer => layer.option_type !== 'prop'),
      ...expressionLayers,
      ...(runtimeDevice ? [{ ...runtimeDevice, z_index: 85 }] : []),
    ].sort((a, b) => a.z_index - b.z_index) as AssetLayer[];
  }, [appearance, baseType, device, family, runtimeState]);

  const toneLayer = layers.find(layer => layer.option_type === 'tone');
  const name = agent.nickname || agent.title;
  const speedStyle = {
    '--character-breathe-duration': 4.8 / animationSpeed + 's',
    '--character-blink-duration': (family === 'owl' ? 7.2 : 5.2) / animationSpeed + 's',
    '--character-idle-duration': 6.4 / animationSpeed + 's',
    '--character-work-duration': 1.25 / animationSpeed + 's',
    '--character-pulse-duration': 1.8 / animationSpeed + 's',
  } as CSSProperties;

  useLayoutEffect(() => {
    if (!compositionStarted || typeof requestAnimationFrame === 'undefined') return;

    const frame = requestAnimationFrame(() => {
      const elapsed = performance.now() - compositionStarted;
      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas.dataset.composeMs = elapsed.toFixed(1);
      canvas.dataset.composeBudget = elapsed < 100 ? 'pass' : 'fail';
      if (elapsed >= 100) {
        console.warn(
          'Character Studio preview exceeded the 100ms composition budget:',
          elapsed.toFixed(1) + 'ms',
        );
      }
    });

    return () => cancelAnimationFrame(frame);
  });

  if (baseType === 'default_agent') {
    return (
      <div className="live-character-preview">
        <div ref={canvasRef} className={'live-preview-canvas default-agent-canvas runtime-' + runtimeState}
          data-family="default_agent" data-gender={gender?.toLowerCase() || 'masculine'}
          data-fixed-portrait={appearance.robotVariantId ? 'true' : 'false'}
          role="img" aria-label={name + ' AgentOrdinary robot preview, ' + runtimeState.replaceAll('_', ' ')}
          style={speedStyle}>
          <div className="character-state-motion">
            <div className="character-rig-motion">
              <DefaultAgentRobot appearance={appearance} runtimeState={runtimeState}/>
            </div>
          </div>
          {runtimeState === 'waiting_on_owner' && <span className="character-runtime-cue">Needs you</span>}
          {runtimeState === 'stalled' && <span className="character-runtime-cue">Stalled</span>}
          {runtimeState === 'failed' && <span className="character-runtime-cue">Needs attention</span>}
          {runtimeState === 'in_review' && <span className="character-runtime-cue">In review</span>}
        </div>
        <div className="live-preview-meta">
          <strong>{name}</strong>
          <span>AgentOrdinary · {gender || 'Masculine'} · {runtimeState.replaceAll('_',' ')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="live-character-preview">
      <div
        ref={canvasRef}
        className={'live-preview-canvas runtime-' + runtimeState}
        data-family={family}
        data-gender={gender?.toLowerCase() || 'generic'}
        data-idle-personality={(appearance.idlePersonality || 'Focused').toLowerCase()}
        role="img"
        aria-label={name + ' live character preview, ' + runtimeState.replaceAll('_', ' ')}
        style={speedStyle}
      >
        <div className="character-state-motion">
          <div className="character-rig-motion">
            {family === 'bookworm' ? (
              <BookwormApprovedArt appearance={appearance.autoWorkGlasses ? {...appearance,eyewear:runtimeState==='working'?'On':'Off'} : appearance} />
            ) : layers.map(layer => {
              const runtimeDevice =
                runtimeState === 'working' &&
                layer.option_type === 'prop' &&
                layer.option_value === device;

              return (
                <svg
                  key={[
                    layer.family,
                    layer.option_type,
                    layer.option_value,
                    layer.layer_file,
                  ].join(':')}
                  className={
                    'character-layer layer-' +
                    layer.option_type +
                    (runtimeDevice ? ' runtime-device-layer' : '')
                  }
                  viewBox="0 0 320 360"
                  aria-hidden="true"
                  style={{
                    zIndex: layer.z_index,
                    color:
                      layer.option_type === 'body_detail'
                        ? toneLayer?.color || '#92775e'
                        : layer.color || '#9aabb7',
                  }}
                >
                  {family === 'owl' && layer.option_type === 'body' ? (
                    <image
                      href="/character-assets/owl-approved-static.webp"
                      x="20"
                      y="20"
                      width="280"
                      height="280"
                      preserveAspectRatio="xMidYMid meet"
                      transform={layerTransform(family, layer, gender)}
                    />
                  ) : (
                    <use
                      href={layer.layer_file}
                      transform={layerTransform(family, layer, gender)}
                    />
                  )}
                </svg>
              );
            })}

            <svg
              className="character-blink-layer"
              viewBox="0 0 320 360"
              aria-hidden="true"
            >
              <g className="character-eyelids">
                {family === 'owl' ? (
                  <>
                    <ellipse
                      cx={face.x - 28}
                      cy={face.y}
                      rx="27"
                      ry="31"
                      fill="#fff9ee"
                    />
                    <ellipse
                      cx={face.x + 28}
                      cy={face.y}
                      rx="27"
                      ry="31"
                      fill="#fff9ee"
                    />
                    <path
                      d={
                        'M' +
                        (face.x - 51) +
                        ' ' +
                        (face.y + 1) +
                        'q23 14 46 0M' +
                        (face.x + 5) +
                        ' ' +
                        (face.y + 1) +
                        'q23 14 46 0'
                      }
                      fill="none"
                      stroke="#4b170f"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                  </>
                ) : (
                  <>
                    <ellipse
                      cx={face.x - 17}
                      cy={face.y}
                      rx="10"
                      ry="7"
                      fill={toneLayer?.color || '#dce3e8'}
                    />
                    <ellipse
                      cx={face.x + 17}
                      cy={face.y}
                      rx="10"
                      ry="7"
                      fill={toneLayer?.color || '#dce3e8'}
                    />
                    <path
                      d={
                        'M' +
                        (face.x - 26) +
                        ' ' +
                        face.y +
                        'h18m16 0h18'
                      }
                      fill="none"
                      stroke="#182129"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                    />
                  </>
                )}
              </g>
            </svg>
          </div>
        </div>

        {runtimeState === 'waiting_on_owner' && (
          <span className="character-runtime-cue">Needs you</span>
        )}
        {runtimeState === 'stalled' && (
          <span className="character-runtime-cue">Stalled</span>
        )}
        {runtimeState === 'failed' && (
          <span className="character-runtime-cue">Needs attention</span>
        )}
        {runtimeState === 'in_review' && (
          <span className="character-runtime-cue">In review</span>
        )}

        {!rigged && (
          <span className="character-art-placeholder">
            {family.replace('_', ' ')} art pending
          </span>
        )}
      </div>

      <div className="live-preview-meta">
        <strong>{name}</strong>
        <span>
          {[
            baseType === 'library_animal'
              ? appearance.species || 'Owl'
              : baseType.replace('_', ' '),
            gender,
            appearance.pronouns,
            runtimeState.replaceAll('_', ' '),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>
    </div>
  );
}
